import {
  dirDelta,
  opposite,
  type Direction,
  type GridPosition,
} from "../models/ModuleGrid.js";
import { footprintCells } from "../data/moduleFootprints.js";
import type { ModuleGridController } from "../systems/ModuleGridController.js";

export interface ActiveFlowGroup {
  /** 이 흐름을 만든 Start endpoint 좌표. 같은 Start는 중복 lane이어도 1개 흐름으로만 센다. */
  start: GridPosition;
  /** 해당 Start에서 도달 가능한 모듈 인스턴스 id. 같은 Start 내부에서는 사이클/다중셀이어도 1회만 포함한다. */
  instanceIds: string[];
  /** instanceIds와 같은 순서의 모듈 정의 id. 전투 효과 적용용. */
  definitionIds: string[];
}

export interface PathResult {
  /** Start에서 포트 연결 탐색이 계산됐는지. Fire/목적지 도달 판정이 아니며 정상 lane이면 true다. */
  connected: boolean;
  /** 표시/상태용 active 인스턴스 id — Start 중 하나 이상과 연결된 모든 모듈. 인스턴스 기준 unique. */
  activeInstanceIds: string[];
  /** activeInstanceIds와 같은 순서의 정의 id — 표시명/QA 조회 키. 인스턴스 기준 unique. */
  activeDefinitionIds: string[];
  /** 전투 효과 적용 흐름 — 서로 다른 Start마다 독립 적용한다. 합류 모듈은 여러 flow에 반복 포함될 수 있다. */
  activeFlowGroups: ActiveFlowGroup[];
  /** 구 규칙 호환 필드. 새 규칙에서는 Start와 연결되면 모두 active라 비어야 한다. */
  waterOnlyInstanceIds: string[];
  /** 발동 경로 색칠 셀(시안) — active 흐름 + 그 포트가 향한 인접 빈칸 1칸. */
  activeCells: GridPosition[];
  /** 구 규칙 호환 필드. 새 규칙에서는 Start 연결망 전체가 activeCells라 비어야 한다. */
  waterOnlyCells: GridPosition[];
  /** 물이 닿지 않는(연결 안 된) 배치 모듈 id — 잘못 놓여 물길에 못 낀 파이프(빨강 표시). */
  blockedInstanceIds: string[];
  /** 빨강 색칠 셀 — blocked 모듈의 footprint 셀. 빈칸은 포함 X(모듈만 "잘못된 파이프"). */
  blockedCells: GridPosition[];
}

/** 탐색 순서 — right→up→down→left (지시문 §6: 기본 좌→우 흐름 우선). apply 순서의 tie-break. */
const SEARCH_ORDER: readonly Direction[] = ["right", "up", "down", "left"];

const key = (x: number, y: number): string => `${x},${y}`;
const pathCache = new WeakMap<ModuleGridController, { readonly revision: number; readonly result: PathResult }>();

function parseKey(k: string): { x: number; y: number } {
  const comma = k.indexOf(",");
  return { x: Number(k.slice(0, comma)), y: Number(k.slice(comma + 1)) };
}

/** 파이프(물이 흐르는 실체) — 배치 모듈 또는 endpoint. 빈칸은 파이프가 아니다. */
function isPipe(grid: ModuleGridController, x: number, y: number): boolean {
  return grid.moduleCoveringCell(x, y) !== null || grid.isStart(x, y) || grid.isFire(x, y);
}

/**
 * 색칠할 흐름 셀 — 파이프(모듈/endpoint) 자체 + 그 파이프의 열린 포트가 향한 인접 빈칸 1칸(leak).
 * 빈칸은 graph node가 아니라(pathPortsAt=[]) visited에 들어오지 않는다 — 여기서 pipe node를 순회하며
 * 인접 빈칸/잠금칸을 leak 대상으로 추가한다(빈칸끼리 전파 X, 1칸 한정 — 시각만). visited는 pipe node 집합.
 */
function waterCells(grid: ModuleGridController, visited: Set<string>): GridPosition[] {
  const result: GridPosition[] = [];
  const seen = new Set<string>();
  const add = (x: number, y: number): void => {
    const k = key(x, y);
    if (seen.has(k)) return;
    seen.add(k);
    result.push({ x, y });
  };
  for (const k of visited) {
    const { x, y } = parseKey(k);
    add(x, y); // 파이프 자체 (visited는 전부 pipe node)
    for (const d of grid.pathPortsAt(x, y)) {
      const { dx, dy } = dirDelta(d);
      const nx = x + dx;
      const ny = y + dy;
      if (!grid.inBounds(nx, ny)) continue; // 그리드 밖은 leak 안 함
      if (isPipe(grid, nx, ny)) continue; // 파이프는 visited(별도)거나 미연결(물 안 받음)
      add(nx, ny); // 빈칸/잠금칸 = leak 1칸 (시각만)
    }
  }
  return result;
}

/**
 * 포트 연결 BFS — Start에서 물이 퍼지는 path node 집합/순서 (실제 물 시뮬레이션).
 * path node(endpoint/배치모듈 — isPathNode)만 queue에 넣는다. 그 외 빈칸/잠금칸은
 * graph node가 아니라 전파 안 됨.
 */
function flood(grid: ModuleGridController, start: GridPosition): { visited: Set<string>; order: GridPosition[] } {
  const visited = new Set<string>([key(start.x, start.y)]);
  const queue: GridPosition[] = [{ x: start.x, y: start.y }];
  const order: GridPosition[] = [];

  while (queue.length > 0) {
    const cur = queue.shift()!;
    order.push(cur);
    const curPorts = grid.pathPortsAt(cur.x, cur.y);
    for (const d of SEARCH_ORDER) {
      if (!curPorts.includes(d)) continue;
      const { dx, dy } = dirDelta(d);
      const nx = cur.x + dx;
      const ny = cur.y + dy;
      const k = key(nx, ny);
      if (visited.has(k)) continue;
      if (!grid.isPathNode(nx, ny)) continue; // path node만 전파 (endpoint/배치 모듈)
      if (!grid.pathPortsAt(nx, ny).includes(opposite(d))) continue; // 포트 상호 연결 검증
      visited.add(k);
      queue.push({ x: nx, y: ny });
    }
  }
  return { visited, order };
}

/**
 * 물이 닿지 않는 배치 모듈("잘못된 파이프") — Start 연결망에 없는 placed 모듈 + 그 footprint 셀(빨강).
 * 배치됐는데 물길에 못 낀 파이프(포트 방향 틀림·고립). 빈칸은 대상 아님(모듈만 잘못될 수 있음).
 */
function blockedModules(
  grid: ModuleGridController,
  reachedIds: Set<string>,
): { blockedInstanceIds: string[]; blockedCells: GridPosition[] } {
  const blockedInstanceIds: string[] = [];
  const blockedCells: GridPosition[] = [];
  for (const tile of grid.placed()) {
    if (reachedIds.has(tile.instanceId)) continue;
    blockedInstanceIds.push(tile.instanceId);
    for (const c of footprintCells(tile.footprintId)) {
      blockedCells.push({ x: tile.x + c.dx, y: tile.y + c.dy });
    }
  }
  return { blockedInstanceIds, blockedCells };
}

/**
 * 포트 연결 기반 BFS — Start에서 흐르는 물이 도달하는 셀·모듈.
 *
 * 발동(active) = Start와 포트로 연결된 모든 배치 모듈. Fire 도달 요구는 폐기됐고, 막다른 가지도 연결돼 있으면
 * 모두 발동한다. 배치됐지만 물 안 닿는 모듈은 **blocked**(잘못된 파이프, 빨강). 색칠 = path node +
 * 포트가 향한 인접 빈칸 1칸(waterCells).
 *
 * path node =
 * Start(["right"]) + 저장 호환용 Fire(["left"]) endpoint + 배치모듈.
 * 그 외 빈칸/잠금칸은 전파 노드 아님. 빈칸은 모듈 사이를 이어 주지 않는다.
 * 포트 안 맞는 파이프는 벽이라 길목을 막을 수 있다(blocked). 기본 발사체는 항상 유지(resolveChapterSpec).
 */
function computeSingleLanePath(grid: ModuleGridController, start: GridPosition, fire: GridPosition): PathResult {
  const real = flood(grid, start);
  // 흐름 순(BFS 방문 순)으로 모듈을 active로 수집 (첫 등장 1회만 — 다중 셀 모듈 중복 차단).
  const collect = (): { activeInstanceIds: string[]; activeDefinitionIds: string[] } => {
    const activeInstanceIds: string[] = [];
    const activeDefinitionIds: string[] = [];
    const seen = new Set<string>();
    for (const c of real.order) {
      if ((c.x === start.x && c.y === start.y) || (c.x === fire.x && c.y === fire.y)) continue;
      const cover = grid.moduleCoveringCell(c.x, c.y);
      if (!cover || seen.has(cover.tile.instanceId)) continue;
      seen.add(cover.tile.instanceId);
      activeInstanceIds.push(cover.tile.instanceId);
      activeDefinitionIds.push(cover.tile.definitionId);
    }
    return { activeInstanceIds, activeDefinitionIds };
  };

  const ids = collect();
  const activeCells = waterCells(grid, real.visited);
  const blocked = blockedModules(
    grid,
    new Set(ids.activeInstanceIds),
  );

  return {
    connected: true,
    activeInstanceIds: ids.activeInstanceIds,
    activeDefinitionIds: ids.activeDefinitionIds,
    activeFlowGroups: ids.activeInstanceIds.length > 0
      ? [{
          start: { ...start },
          instanceIds: [...ids.activeInstanceIds],
          definitionIds: [...ids.activeDefinitionIds],
        }]
      : [],
    waterOnlyInstanceIds: [],
    activeCells,
    waterOnlyCells: [],
    blockedInstanceIds: blocked.blockedInstanceIds,
    blockedCells: blocked.blockedCells,
  };
}

function pushUnique<T>(target: T[], values: readonly T[], keyOf: (value: T) => string): void {
  const keys = new Set(target.map(keyOf));
  for (const value of values) {
    const k = keyOf(value);
    if (keys.has(k)) continue;
    keys.add(k);
    target.push(value);
  }
}

function pushUniqueActiveModules(
  targetInstanceIds: string[],
  targetDefinitionIds: string[],
  instanceIds: readonly string[],
  definitionIds: readonly string[],
): void {
  const seen = new Set(targetInstanceIds);
  for (const [index, instanceId] of instanceIds.entries()) {
    if (seen.has(instanceId)) continue;
    seen.add(instanceId);
    targetInstanceIds.push(instanceId);
    const definitionId = definitionIds[index];
    if (definitionId !== undefined) targetDefinitionIds.push(definitionId);
  }
}

function computePath(grid: ModuleGridController): PathResult {
  const lanes = grid.lanes.length > 0 ? grid.lanes : [{ start: grid.start, fire: grid.fire }];
  const activeInstanceIds: string[] = [];
  const activeDefinitionIds: string[] = [];
  const activeFlowGroups: ActiveFlowGroup[] = [];
  const waterOnlyInstanceIds: string[] = [];
  const activeCells: GridPosition[] = [];
  const waterOnlyCells: GridPosition[] = [];
  const seenStarts = new Set<string>();
  let connected = false;

  for (const lane of lanes) {
    const startKey = key(lane.start.x, lane.start.y);
    if (seenStarts.has(startKey)) continue;
    seenStarts.add(startKey);
    const lanePath = computeSingleLanePath(grid, lane.start, lane.fire);
    connected ||= lanePath.connected;
    pushUniqueActiveModules(
      activeInstanceIds,
      activeDefinitionIds,
      lanePath.activeInstanceIds,
      lanePath.activeDefinitionIds,
    );
    activeFlowGroups.push(
      ...lanePath.activeFlowGroups.map((group) => ({
        start: { ...group.start },
        instanceIds: [...group.instanceIds],
        definitionIds: [...group.definitionIds],
      })),
    );
    pushUnique(waterOnlyInstanceIds, lanePath.waterOnlyInstanceIds, (id) => id);
    pushUnique(activeCells, lanePath.activeCells, (c) => key(c.x, c.y));
    pushUnique(waterOnlyCells, lanePath.waterOnlyCells, (c) => key(c.x, c.y));
  }

  const reached = new Set([...activeInstanceIds, ...waterOnlyInstanceIds]);
  const blocked = blockedModules(grid, reached);
  return {
    connected,
    activeInstanceIds,
    activeDefinitionIds,
    activeFlowGroups,
    waterOnlyInstanceIds,
    activeCells,
    waterOnlyCells: waterOnlyCells.filter((c) => !activeCells.some((a) => a.x === c.x && a.y === c.y)),
    blockedInstanceIds: blocked.blockedInstanceIds,
    blockedCells: blocked.blockedCells,
  };
}

export function findPath(grid: ModuleGridController): PathResult {
  const cached = pathCache.get(grid);
  if (cached?.revision === grid.revision) return cached.result;
  const result = computePath(grid);
  pathCache.set(grid, { revision: grid.revision, result });
  return result;
}
